import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaCloudUploadAlt } from "react-icons/fa";
import RequiredAsterisk from "../UI/RequiredAsterisk";
import uploadFile from "./uploadToFileShare";
import fileUploadHelper from "../../../global/utils/fileUploadHelper";

import ui_utils from "../UI/uiUtils";
import file_utils from "./fileUtils";

const FileUpload = (props) => {
  const {
    fileTypes,
    machine_name,
    name,
    dontShowAsterisk,
    update_time,
    accept_multiple,
    accept_multiple_warning_message,
    special_warning_message,
    uploadTitle,
    documentAuth,
  } = props.schemaProps;
  const application_id = props.appId;
  let showRequired = dontShowAsterisk ? false : props.showRequired;

  // store files in local state
  // may need for deleting (post MVP)
  const [pending_files, set_pending_files] = useState([]);
  const [files, setFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [warningMessage, setWarningMessage] = useState(false);

  const updateFileCollection = (_file) => {
    let existsInState = false;
    let _nextFiles = [];
    let _completedFiles = []; //if the file is 100 uploaded, add it, dispatch a state update with the list of file names

    if (_file && _file.state && _file.state === "failed") {
      _nextFiles = files.filter((file) => file.document_name !== _file.name);
      setErrorMessage(
        _file.name + " has failed to upload. Please try again later."
      );
      setFiles(_nextFiles);

      return true;
    }

    if (files.length === 0) {
      _nextFiles.push(_file);
    } else {
      files.map((file) => {
        if (
          file.name === _file.name &&
          file.lastModified === _file.lastModified
        ) {
          _nextFiles.push(_file);
          existsInState = true;
        } else {
          _nextFiles.push(file);
        }
        return true;
      });

      if (!existsInState) _nextFiles.push(_file);
    }

    _completedFiles = _nextFiles.filter((file) => file.percentage === 100);
    _completedFiles = _completedFiles.map((file) => {
      return Object.assign(file, { fileTypes });
    });

    if (_file.percentage === 100) {
      setErrorMessage(false);
      if (update_time) {
        let current_date = new Date();
        props.formApi.change(update_time, current_date.toISOString());
      }
    }

    if (props.fieldArray) {
      props.addToModalState(_completedFiles);
      setFiles(_completedFiles);
    } else {
      setFiles(_completedFiles);
      props.input.onChange(_completedFiles);
    }
  };

  // take files accepted from file input
  // and upload to fileshare
  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles) {
      let currentFiles =
        props && props.formApi && props.formApi.getFieldState("files")
          ? props.formApi.getFieldState("files").value
          : props.formApi.getState().values.files;
      currentFiles =
        currentFiles &&
        currentFiles.filter((file) => file.machine_name === machine_name);
      if (
        accept_multiple !== undefined &&
        accept_multiple === false &&
        ((currentFiles && currentFiles.length === 1) ||
          (files && files.length === 1))
      ) {
        setWarningMessage(
          accept_multiple_warning_message
            ? accept_multiple_warning_message
            : "You may only upload one file."
        );
        return;
      }

      for (var i = 0; i < acceptedFiles.length; i++) {
        if (!fileUploadHelper(acceptedFiles[i].name)) {
          setWarningMessage(
            special_warning_message
              ? special_warning_message
              : `File could not be uploaded. The filename cannot contain special characters. Please remove any of the following characters from the filename if they exist:  " \\ / : | < > * ?`
          );
          return;
        } else {
          setWarningMessage(false);
        }
      }
      set_pending_files(acceptedFiles.map((file) => file));

      acceptedFiles.map((file) => {
        uploadFile(
          file,
          fileTypes,
          application_id,
          updateFileCollection,
          props.row ? machine_name + props.row : machine_name,
          name,
          documentAuth,
          props.page_index
        );
      });
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    acceptedFiles,
    rejectedFiles,
  } = useDropzone({
    onDrop: (acceptedFiles) => handleDrop(acceptedFiles),
    noKeyboard: false,
    maxSize: 31457280, //30MB
    accept: file_utils.getMimeTypes(fileTypes),
    multiple: false,
  });

  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    padding: "20px",
    borderWidth: 1,
    borderRadius: 2,
    borderColor: "#aeb0b5",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    marginBottom: "10px",
    transition: "border .24s ease-in-out",
  };

  const activeStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#04c585",
    borderWidth: "2px",
  };

  const rejectStyle = {
    borderColor: "#e41d3d",
    borderWidth: "2px",
  };

  const dropLabelStyle = {
    color: "#000",
    margin: "3px 0 0 0",
  };

  const linkStyle = {
    color: "#005ea2",
    textDecoration: "underline",
    cursor: "pointer",
  };

  const iconStyle = {
    marginTop: "-2px",
    marginRight: "15px",
  };

  const filePathStyle = {
    fontStyle: "italic",
    marginRight: "20px",
  };

  const fileSizeStyle = {
    color: "#aeb0b5",
  };

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject]
  );

  const fileDisplayStyle = {
    padding: "0 20px 0 20px",
    margin: "15px 0 20px 0",
    border: "1px dashed #aeb0b5",
    backgroundColor: "#fafafa",
  };

  const deselectFile = (_file) => {
    let { machine_name, fileType, document_name, _lastModified } = _file;
    let _key = `${machine_name}${document_name}`;

    let tmp_files =
      props && props.formApi && props.formApi.getFieldState("files")
        ? props.formApi.getFieldState("files").value
        : props.formApi.getState().values.files;
    let _files = files.filter((file) => {
      let fileKey = `${file.machine_name}${file.document_name}`;
      return fileKey !== _key;
    });
    setFiles(_files);
    let currentFiles =
      props && props.formApi && props.formApi.getFieldState("files")
        ? props.formApi.getFieldState("files").value
        : props.formApi.getState().values.files;
    if (
      accept_multiple !== undefined &&
      accept_multiple === false &&
      ((currentFiles && currentFiles.length === 1) ||
        (files && files.length === 1))
    ) {
      setWarningMessage(false);
    }
    props.input.onChange(_files);
    if (tmp_files) {
      let _nextFiles = tmp_files.filter((file) => {
        let fileKey = `${file.machine_name}${file.document_name}`;
        return fileKey !== _key;
      });
      props.formApi.change("files", _nextFiles);
    }
  };

  let file_list = [],
    new_files = [],
    newFilesAdded;
  newFilesAdded = false;
  let file_state =
    props && props.formApi && props.formApi.getFieldState("files")
      ? props.formApi.getFieldState("files").value
      : props.formApi.getState().values.files;
  if (file_state) {
    for (let j = 0; j < file_state.length; j++) {
      if (props.row) {
        if (file_state[j].machine_name === machine_name + props.row) {
          new_files.push(file_state[j]);
        }
      } else {
        if (file_state[j].machine_name === machine_name) {
          new_files.push(file_state[j]);
        }
      }
      if (props.schemaProps.checkFilesAdded) {
        newFilesAdded = true;
      }
    }
  }

  if (files && files.length > 0 && !newFilesAdded) {
    new_files = new_files.concat(files);
  }

  if (new_files) {
    for (let i = 0; i < new_files.length; i++) {
      file_list.push(
        <p key={new_files[i].document_path + i}>
          <span name={machine_name} style={filePathStyle}>
            {new_files[i].document_name}
          </span>
          <span className="removeAttachment">
            <a
              class="usa-link"
              onClick={(e) => {
                e.preventDefault();
                return deselectFile(new_files[i]);
              }}
            >
              Clear
            </a>
          </span>
        </p>
      );
    }
  }
  const rejectedFileList = rejectedFiles.map((file) => (
    <p key={file.path}>
      <span style={filePathStyle}>{file.path}</span>{" "}
      <span style={fileSizeStyle}>{file.size} bytes</span>
    </p>
  ));
  const fileuploadContainer = {
    marginTop: "15px",
    marginBottom: "8px",
    display: "inline-block",
  };
  const warningStyles = {
    backgroundColor: "rgb(233, 233, 233)",
    border: "1px solid rgb(210, 211, 214)",
    padding: "28px 16px 28px 21px",
    width: "100%",
    marginTop: "1rem",
  };

  return (
    <div
      className="fileupload-container"
      key={machine_name}
      id={machine_name}
      name={machine_name}
    >
      {warningMessage ? (
        <div style={warningStyles}>{warningMessage}</div>
      ) : null}
      <label
        style={fileuploadContainer}
        className="usa-label"
        htmlFor={props.machine_name}
      >
        {uploadTitle ? uploadTitle : "Document Upload "}
        {showRequired && <RequiredAsterisk show={showRequired} />}
      </label>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} name={machine_name} />
        <FaCloudUploadAlt style={iconStyle} size={"1.5em"} color={"#aeb0b5"} />
        <p style={dropLabelStyle}>
          Drop files here or{" "}
          <span tabIndex={0} style={linkStyle}>
            browse
          </span>
          .<br />
          <small style={ui_utils.colorGrey}>
            File types allowed:{" "}
            {fileTypes
              ? fileTypes.join(", ")
              : ".pdf, .doc, .docx, .txt, .xlsx"}
            <br />
            File size limit: 30MB
          </small>
        </p>
      </div>
      {file_list && file_list.length > 0 && (
        <aside style={fileDisplayStyle}>{file_list}</aside>
      )}

      {errorMessage && <aside style={fileDisplayStyle}>{errorMessage}</aside>}

      {rejectedFileList && rejectedFileList.length > 0 && (
        <aside style={fileDisplayStyle}>
          <strong>
            This file can not be uploaded. Check the file type or file size.
          </strong>
          {rejectedFileList}
        </aside>
      )}
    </div>
  );
};

export default FileUpload;
